import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Loader2, Shield, Building2 } from 'lucide-react'
import { useAllGroupExtensions, useClientes } from '@/modules/users/hooks/useGroups'
import { TransferList } from './TransferList'
import type { Area, AreaCreateInput, AreaUpdateInput } from '@/modules/users/types/area'

const areaSchema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido'),
  slug: z.string().optional(),
  descripcion: z.string().optional(),
  imagen_url: z.string().optional(),
  orden: z.coerce.number().min(0),
  is_active: z.boolean(),
})

type AreaFormData = z.infer<typeof areaSchema>

interface AreaFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: AreaCreateInput & AreaUpdateInput) => void
  isSubmitting: boolean
  area?: Area | null
  mode: 'create' | 'edit'
}

export function AreaFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  area,
  mode,
}: AreaFormDialogProps) {
  const [laboratorioId, setLaboratorioId] = useState<number | null>(null)
  const [assignedGroupIds, setAssignedGroupIds] = useState<number[]>([])


  const { data: groupExtensions, isLoading: isLoadingGroups } = useAllGroupExtensions()
  const { data: clientes, isLoading: isLoadingClientes } = useClientes()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AreaFormData>({
    resolver: zodResolver(areaSchema),
    defaultValues: {
      nombre: '',
      slug: '',
      descripcion: '',
      imagen_url: '',
      orden: 0,
      is_active: true,
    },
  })

  const isActive = watch('is_active')

  useEffect(() => {
    if (open && mode === 'edit' && area) {
      reset({
        nombre: area.nombre,
        slug: area.slug,
        descripcion: area.descripcion,
        imagen_url: area.imagen_url,
        orden: area.orden,
        is_active: area.is_active,
      })
      setLaboratorioId(area.laboratorio_id)
      setAssignedGroupIds(area.grupos.map((g) => g.id))
    } else if (open && mode === 'create') {
      reset({
        nombre: '',
        slug: '',
        descripcion: '',
        imagen_url: '',
        orden: 0,
        is_active: true,
      })
      setLaboratorioId(null)
      setAssignedGroupIds([])
    }
  }, [open, mode, area, reset])

  const handleFormSubmit = (data: AreaFormData) => {
    const payload = {
      nombre: data.nombre,
      slug: data.slug || undefined,
      descripcion: data.descripcion || '',
      imagen_url: data.imagen_url || '',
      laboratorio_id: laboratorioId,
      grupos: assignedGroupIds,
      orden: data.orden,
      is_active: data.is_active,
    }
    onSubmit(payload)
  }

  const handleGroupTransfer = (itemIds: number[], toAssigned: boolean) => {
    if (toAssigned) {
      setAssignedGroupIds((prev) => [...prev, ...itemIds])
    } else {
      setAssignedGroupIds((prev) => prev.filter((id) => !itemIds.includes(id)))
    }
  }

  const handleLabTransfer = (itemIds: number[], toAssigned: boolean) => {
    if (toAssigned && itemIds.length > 0) {
      setLaboratorioId(itemIds[0])
    } else {
      setLaboratorioId(null)
    }
  }

  // Helper: map GroupExtensionListItem → TransferItem
  const toTransferItem = (ext: { grupo_id: number; grupo_nombre: string }) => ({
    id: ext.grupo_id,
    label: ext.grupo_nombre,
  })

  // Clientes (laboratorios con cliente=true) para el selector de laboratorio
  const assignedLabs = (clientes?.filter((ext) => ext.grupo_id === laboratorioId) || []).map(toTransferItem)
  const unassignedLabs = (clientes?.filter((ext) => ext.grupo_id !== laboratorioId) || []).map(toTransferItem)

  // Regular groups (no laboratorios) para el selector de grupos
  const regularGroups = groupExtensions?.filter((ext) => !ext.laboratorio)
  const assignedGroups = (regularGroups?.filter((ext) => assignedGroupIds.includes(ext.grupo_id)) || []).map(toTransferItem)
  const unassignedGroups = (regularGroups?.filter((ext) => !assignedGroupIds.includes(ext.grupo_id)) || []).map(toTransferItem)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Crear Área' : 'Editar Área'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Configure el área con su laboratorio y grupos asociados'
              : 'Actualice la configuración del área'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                id="nombre"
                {...register('nombre')}
                placeholder="Ej: Tonal, Admin, Medifarma"
                disabled={isSubmitting}
              />
              {errors.nombre && (
                <p className="text-sm text-destructive">{errors.nombre.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">
                Slug <span className="text-xs text-muted-foreground">(auto-generado si vacío)</span>
              </Label>
              <Input
                id="slug"
                {...register('slug')}
                placeholder="ej: tonal, admin"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Input
              id="descripcion"
              {...register('descripcion')}
              placeholder="Descripción breve del área"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="imagen_url">URL de Imagen</Label>
              <Input
                id="imagen_url"
                {...register('imagen_url')}
                placeholder="/logo_tonal.png"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="orden">Orden</Label>
              <Input
                id="orden"
                type="number"
                {...register('orden')}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="is_active">Área Activa</Label>
              <p className="text-sm text-muted-foreground">
                Las áreas inactivas no se muestran a los usuarios
              </p>
            </div>
            <Switch
              id="is_active"
              checked={isActive}
              onCheckedChange={(checked) => setValue('is_active', checked)}
              disabled={isSubmitting}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>

          {/* Laboratorio Selection (solo clientes activos) */}
          {isLoadingClientes ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <TransferList
              title="Laboratorio"
              icon={Building2}
              description="Selecciona el laboratorio principal de esta área (máximo 1)"
              availableItems={unassignedLabs}
              assignedItems={assignedLabs}
              onTransfer={handleLabTransfer}
              disabled={isSubmitting}
            />
          )}

          {/* Groups Selection */}
          {isLoadingGroups ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <TransferList
              title="Grupos"
              icon={Shield}
              description="Grupos que se asignarán automáticamente a los usuarios de esta área"
              availableItems={unassignedGroups}
              assignedItems={assignedGroups}
              onTransfer={handleGroupTransfer}
              disabled={isSubmitting}
            />
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-teal-800 hover:bg-teal-900 text-white">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Crear Área' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
